<template>
    <div id="chatCloseSettingsButton">
        <VisitorBanModal
            v-if="visitorBanModal"
            :visitor="contact"
            :ban-modal-visible="visitorBanModal"
            @changed="visitorBanUpdated"
            @close="visitorBanModal = false"
        />
        <PrivateMessage
            v-if="privateMessage"
            :conversation-id="contact.lastConversation._id"
            @close="privateMessage = false"
        />
        <Modal2
            v-if="assignAgent"
            id="assign-agent"
            :title="$t('message.addRepresentative')"
            :show-footer="false"
            @cancel="assignAgent = false"
        >
            <AgentList :assign="true" :show-online-agents="true" @close="() => closeModal('assign')" />
        </Modal2>
        <Modal2
            v-if="forwardAgent"
            id="forward-agent"
            :title="$t('message.forwardChat')"
            :show-footer="false"
            @cancel="forwardAgent = false"
        >
            <ChatForwardWindow :contact="contact" @close="forwardAgent = false" />
        </Modal2>
        <Modal2
            v-if="showSessionTerminationModal"
            @cancel="handleSessionCancel"
            :title="$t('message.endChatWizard')"
            :showFooter="false"
            xlarge
            :top="10"
            className="session-termination-modal"
        >
            <SessionTermination :contact="contact" @terminate="handleSessionTerminate" @cancel="handleSessionCancel" />
        </Modal2>

        <SendCapiEventModal
            v-if="showCapiEventModal"
            :visible="showCapiEventModal"
            :contact="contact"
            @cancel="showCapiEventModal = false"
        />

        <Modal2
            v-if="showWebphoneSelectionModal"
            @cancel="cancelWebphoneSelection"
            @ok="confirmWebphoneSelection"
            :title="$t('chat.chatCloseSettings.selectPhone')"
            :description="$t('chat.chatCloseSettings.selectPhoneDescription', { number: pendingPhoneNumber })"
            :okText="$t('chat.chatCloseSettings.call')"
            :cancelText="$t('message.cancel')"
            className="webphone-selection-modal"
        >
            <div class="webphone-channel-list">
                <div
                    v-for="channel in availableWebphoneChannels"
                    :key="channel._id"
                    class="channel-option"
                    :class="{ selected: selectedChannelId === channel._id }"
                    @click="selectedChannelId = channel._id"
                >
                    <div class="channel-radio">
                        <input
                            type="radio"
                            :value="channel._id"
                            v-model="selectedChannelId"
                            :id="`channel-${channel._id}`"
                        />
                    </div>
                    <div class="channel-icon">
                        <img
                            v-if="channel.type === 'webphone-telnyx'"
                            src="/static/img/telnyx.png"
                            alt="Telnyx"
                        />
                        <img
                            v-else-if="channel.type === 'webphone-netgsm'"
                            src="/static/img/netgsm.png"
                            alt="NetGSM"
                        />
                        <i
                            v-else
                            class="material-icons webphone-icon"
                        >
                            phone
                        </i>
                    </div>
                    <div class="channel-info">
                        <div class="channel-name">{{ channel.name || $t('chat.chatCloseSettings.unnamedChannel') }}</div>
                        <div class="channel-details">
                            <span class="channel-type">{{ getChannelTypeDisplay(channel.type) }}</span>
                            <span
                                v-if="$_.get(channel, 'payload.callerPhoneNumber')"
                                class="channel-phone"
                            >
                                • {{ channel.payload.callerPhoneNumber }}
                            </span>
                        </div>
                    </div>
                    <div class="channel-arrow">
                        <i class="material-icons">chevron_right</i>
                    </div>
                </div>
            </div>
        </Modal2>
        <div class="user-meta" @click="viewContactProfile">
            <ContactAvatar :contact="contact" :size="'smallest'" />
            <div class="chat-contact-details-wrapper">
                <div
                    ref="visitorNameInput"
                    class="chat-contact-info chat-visitor-name-field"
                    @keydown="handleVisitorNameInputKeyDown"
                    @keyup="handleVisitorNameInputKeyUp"
                    :placeholder="$t('chat.chatCloseSettings.typeVisitorName')"
                    v-tippy="{ followCursor: true, placement: 'bottom', delay: [300, 0] }"
                    :content="getFullname()"
                >
                    <div
                        class="material-icons vip"
                        v-if="contact.vipCustomer"
                        v-tippy="{ followCursor: true, placement: 'bottom-end', delay: [600, 0] }"
                        :content="$t('chat.chatCloseSettings.vipCustomerTooltip')"
                    >
                        star
                    </div>
                    <span class="visitor-name-text">{{ getFullname() }}</span>
                </div>
                <div class="chat-contact-info chat-visitor-platform-field">
                    <div class="chat-platform-info-wrapper">
                        <PlatformIcon :platform="channelType" :size="12" :tippy-disabled="true" />
                        <p
                            class="chat-platform-text-label"
                            v-tippy="{ followCursor: true, placement: 'bottom', delay: [300, 0] }"
                            :content="platformTooltip"
                        >
                            {{ platformTooltip }}
                        </p>
                        <template v-if="contact.email && contact.email !== ''">
                            <span class="chat-platform-separator">-</span>
                            <div class="material-icons chat-contact-icon">mail</div>
                            <span
                                v-if="hasUserMail()"
                                class="chat-platform-email-link"
                                v-tippy="{ followCursor: true, placement: 'bottom', delay: [300, 0] }"
                                :content="getEmail()"
                            >
                                <a :href="'mailto:' + getEmail()">{{ getEmail() }}</a>
                            </span>
                            <span
                                v-else
                                class="chat-platform-phone-link"
                                v-tippy="{ followCursor: true, placement: 'bottom', delay: [300, 0] }"
                                :content="getChannelNumber()"
                            >
                                <a :href="'tel:' + getChannelNumber()">{{ getChannelNumber() }}</a>
                            </span>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <div class="conversation-actions">
            <ContactProfileAutomationButton :contact-id="contact._id" :show-text="false" />
            <AiVoiceAgentCallStarter :contact="contact" :default-to-number="contact.phone || contact.whatsappNumber" />
            <div
                class="conversation-actions-button webphone-call"
                @click="startWebphoneCall"
                v-if="contact.phone || contact.whatsappNumber"
                v-tippy="{ followCursor: true, placement: 'bottom' }"
                :content="$t('chat.chatCloseSettings.webphoneCall')"
            >
                <div class="material-icons" style="color:white !important;">phone</div>
            </div>
            <SupsisButton v-if="isShowing('join-conversation')" primary class="d-none">
                {{ $t("chatWindow.watching") }}
            </SupsisButton>

            <div
                class="call-features"
                v-else-if="
                    isShowing('call-features') &&
                    !videoCallMode &&
                    !contact.whatsappNumber &&
                    !contact.instagramUserId &&
                    !contact.messengerUserId &&
                    !contact.telegramUserId
                "
            >
                <div class="conversation-actions-button call" @click="$feature.beforeStartVoiceCall(callVoiceCall)">
                    <div class="material-icons">call</div>
                </div>
                <div
                    class="conversation-actions-button video-call"
                    @click="$feature.beforeStartVideoCall(callVideoCall)"
                >
                    <div class="material-icons">video_call</div>
                </div>
            </div>

            <a-popover
                overlayClassName="actionSettingsButton"
                v-model="isPopoverVisible"
                data-v-step="17"
                placement="bottomRight"
                title=""
                trigger="click"
            >
                <template #content>
                    <div class="more-actions" v-if="user">
                        <div class="action-button" v-if="isShowing('assign-agent')" @click="assignRep">
                            <div class="action-button-icon">
                                <div class="material-icons">person_add</div>
                            </div>
                            <div class="action-button-text">{{ $t("message.assignRep") }}</div>
                        </div>
                        <div class="action-button" v-if="isShowing('auto-translate')">
                            <div class="action-button-icon">
                                <div class="material-icons">translate</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t("message.autoTranslate") }} :
                                <a-switch
                                    :checked="autoTranslateEnabled"
                                    @change="$emit('autoTranslateChanged', $event)"
                                    class="ml-2 mr-2"
                                    size="small"
                                />
                            </div>
                        </div>
                        <div class="action-button">
                            <div class="action-button-icon">
                                <div class="material-icons">visibility_off</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t('chat.chatCloseSettings.hideSystemMessages') }} :
                                <a-switch
                                    :checked="hideSystemMessages"
                                    @change="toggleHideSystemMessages"
                                    class="ml-2 mr-2"
                                    size="small"
                                />
                            </div>
                        </div>
                        <div class="action-button">
                            <div class="action-button-icon">
                                <div class="material-icons">bug_report</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t('chat.chatCloseSettings.chatbotDebugMode') }} :
                                <a-switch
                                    :checked="debugModeEnabled"
                                    @change="$emit('debugModeChanged', $event)"
                                    class="ml-2 mr-2"
                                    size="small"
                                />
                            </div>
                        </div>
                        <div class="action-button" v-if="isShowing('leave-chat')" @click="leave">
                            <div class="action-button-icon">
                                <div class="material-icons">logout</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t("message.leaveChat") }}
                            </div>
                        </div>
                        <div class="action-button" @click="createTask">
                            <div class="action-button-icon">
                                <div class="material-icons">add_task</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t('chat.chatCloseSettings.createTask') }}
                                <span class="keyboard-shortcut">Ctrl T</span>
                            </div>
                        </div>
                        <div class="action-button" @click="toggleAnswerStatus" v-if="canToggleAnswerStatus">
                            <div class="action-button-icon">
                                <div class="material-icons">{{ answerStatusIcon }}</div>
                            </div>
                            <div class="action-button-text">
                                {{ answerStatusButtonText }}
                                <span class="keyboard-shortcut">C</span>
                            </div>
                        </div>
                        <div class="action-button" @click="toggleUnread">
                            <div class="action-button-icon">
                                <div class="material-icons">{{ unreadStatusIcon }}</div>
                            </div>
                            <div class="action-button-text">
                                {{ unreadStatusButtonText }}
                            </div>
                        </div>
                        <div class="action-button" v-if="isShowing('forward-agent')" @click="forwardRep">
                            <div class="action-button-icon">
                                <div class="material-icons">person_4</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t("message.forwardRep") }}
                                <span class="keyboard-shortcut">Ctrl F</span>
                            </div>
                        </div>
                        <div class="action-button" v-if="isShowing('private-message')" @click="pm">
                            <div class="action-button-icon">
                                <div class="material-icons">mail_lock</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t("message.privateMessage_") }}
                                <span class="keyboard-shortcut">Ctrl P</span>
                            </div>
                        </div>
                        <div
                            class="action-button"
                            v-if="checkPermission(PERMISSION_TYPES['visitor.block'].tr.name)"
                            @click="ban"
                        >
                            <div class="action-button-icon">
                                <div class="material-icons">block</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t("message.block_") }}
                                <span class="keyboard-shortcut">Ctrl B</span>
                            </div>
                        </div>
                        <div class="action-button" @click="viewContactProfile">
                            <div class="action-button-icon">
                                <div class="material-icons">person</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t('chat.chatCloseSettings.showProfile') }}
                                <span class="keyboard-shortcut">Ctrl O</span>
                            </div>
                        </div>
                        <div
                            class="action-button"
                            @click="openCapiEventModal"
                            v-if="isMetaPlatform()"
                        >
                            <div class="action-button-icon">
                                <div class="material-icons">ads_click</div>
                            </div>
                            <div class="action-button-text">{{ $t('chat.chatCloseSettings.sendCapiEvent') }}</div>
                        </div>
                        <div class="action-button close-chat-button" @click="closeChat" v-if="isShowing('close-chat')">
                            <div class="action-button-icon">
                                <div class="material-icons">power_settings_new</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t("message.endChat") }}
                                <span class="keyboard-shortcut">Ctrl W</span>
                            </div>
                        </div>
                    </div>
                </template>
                <div class="conversation-actions-button more-vert">
                    <div class="material-icons">more_vert</div>
                </div>
            </a-popover>
        </div>
    </div>
</template>
<script>
import CircleCloseButton from "../../svg/CircleCloseButton.vue";
import CONFIG from "../../../config/config";
import { getPersistenceData } from "../../../utils/persistenceStorage";
import * as _ from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import { PHONE_UTILS } from "../../../utils/phone";
import { log2 } from "../../../utils/log2";
import { PHONE } from "../../../webrtc/phoneSDK";
import { sendSocketMessage } from "../../../utils/socketStream";
import { publishMessage } from "../../../utils/systemPubSub";
import { PERMISSION_TYPES } from "../../../language/permissionTypes";
import { capitalize, fakeConversation, maskContactInfo, SanitizeHtml } from "../../../utils/utils";
import VideoCallCloseSvg from "../../svg/VideoCallCloseSvg.vue";
import PowerOff from "../../svg/PowerOff.vue";
import Block from "../../../views/Pages/Chatbot/pages/dashboard/diagram/Block.vue";
import AssignRepresentative from "../../svg/AssignRepresentative.vue";
import OpenChat from "../../svg/OpenChat.vue";
import ForwardRepresentative from "../../svg/ForwardRepresentative.vue";
import MinimizeWindow from "../../svg/MinimizeWindow.vue";
import ChatWindowSettings from "../../svg/ChatWindowSettings";
import SupsisButton from "../../../views/Pages/Settings/components/SupsisButton.vue";
import VisitorBanModal from "../../VisitorBanModal.vue";
import Modal2 from "../../common/Modal2.vue";
import PrivateMessage from "../../PrivateMessage.vue";
import AgentList from "../../AgentList.vue";
import ContactAvatar from "./ContactAvatar.vue";
import ChatForwardWindow from "../../../views/Pages/Chat/components/ChatForwardWindow.vue";
import { AutomationEventTypes } from "../../../views/Pages/Automation/utils/AutomationModuleTypes";
import ContactProfileAutomationButton from "../../common/ContactProfileAutomationButton.vue";
import SessionTermination from "./SessionTermination.vue";
import confetti from "canvas-confetti";
import AiVoiceAgentCallStarter from "../../AiVoiceAgentCallStarter.vue";
import SYSTEM_PUBSUB from "../../../utils/systemPubSub";
import SendCapiEventModal from "../../capi/SendCapiEventModal.vue";
import PlatformIcon from "./ChatMessageArea/components/PlatformIcon.vue";

const log = log2("ChatCloseSettingsButton");
export default {
    name: "ChatCloseSettingsButton",
    components: {
        ContactAvatar,
        AgentList,
        PrivateMessage,
        Modal2,
        VisitorBanModal,
        SupsisButton,
        MinimizeWindow,
        ForwardRepresentative,
        OpenChat,
        AssignRepresentative,
        Block,
        CircleCloseButton,
        VideoCallCloseSvg,
        PowerOff,
        ChatWindowSettings,
        ChatForwardWindow,
        ContactProfileAutomationButton,
        SessionTermination,
        AiVoiceAgentCallStarter,
        SendCapiEventModal,
        PlatformIcon,
    },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        autoTranslateEnabled: {
            type: Boolean,
            default: false,
        },
        debugModeEnabled: {
            type: Boolean,
            default: false,
        },
        isWatching: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapState({
            autoTranslate: (state) => state.chat.autoTranslate,
            isRoot: (state) => state.auth.user.role.isRoot,
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            videoCallMode: (state) => state.webrtc.videoCallMode,
            videoCallFeatureEnabled: (state) => _.get(state, "setting.settings.videocallEnabled", true),
            audioCallFeatureEnabled: (state) => _.get(state, "setting.settings.audiocallEnabled", true),
            settings: (state) => state.setting.settings,
            hideSystemMessages: (state) => state.app.hideSystemMessages,
            channels: (state) => state.channel.channels || [],
            CustomLoginInputs: (state) =>
                state.customLoginForm.inputs.reduce((acc, cur) => {
                    acc[cur._id] = cur;
                    return acc;
                }, {}),
            profileButtonAutomations: (state) =>
                state.automation.automations.filter(
                    (x) =>
                        x.options.trigger === "event" &&
                        x.options.event === AutomationEventTypes.CONTACT.PROFILE_BUTTON_CLICKED &&
                        x.published
                ),
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
            isDarkMode: "user/isDarkMode",
        }),
        channelType() {
            if (this.contact.platform === "whatsapp") {
                const channel = this.channels.find((channel) => channel._id === this.contact.channel);
                return channel ? channel.type : "notWp";
            }
            return this.contact.platform;
        },
        channelName() {
            if (this.contact.platform === "whatsapp") {
                const channel = this.channels.find((channel) => channel._id === this.contact.channel);
                return channel ? channel.name : "";
            }
            return "";
        },
        platformTooltip() {
            if (["whatsapp", "instagram", "instagram-v2", "messenger", "telegram", "linkedin", "google", "calendar", "meta-leads", "trendyol"].includes(this.contact.platform)) {
                const channel = this.channels.find((channel) => channel._id === this.contact.channel);
                if (channel && channel.name) {
                    return channel.name;
                }
            }

            // Channel name yoksa type bilgisini göster
            switch (this.channelType) {
                case "web":
                    return this.$t("chat.chatCloseSettings.platformWeb");
                case "whatsapp":
                case "whatsapp-v2":
                    return this.$t("chat.chatCloseSettings.platformWhatsapp");
                case "whatsapp-business-api":
                case "whatsapp-business-api-supsis":
                    return this.$t("chat.chatCloseSettings.platformWhatsappBusiness");
                case "instagram":
                    return this.$t("chat.chatCloseSettings.platformInstagram");
                case "instagram-v2":
                    return this.$t("chat.chatCloseSettings.platformInstagramV2");
                case "messenger":
                    return this.$t("chat.chatCloseSettings.platformMessenger");
                case "telegram":
                    return this.$t("chat.chatCloseSettings.platformTelegram");
                case "trendyol":
                    return this.$t("chat.chatCloseSettings.platformTrendyol");
                case "email":
                    return this.$t("chat.chatCloseSettings.platformEmail");
                case "sms":
                    return this.$t("chat.chatCloseSettings.platformSms");
                case "notWp":
                    return this.$t("chat.chatCloseSettings.unknown");
                default:
                    return this.channelType || this.$t("chat.chatCloseSettings.unknown");
            }
        },
        canToggleAnswerStatus() {
            const isDestroyed = _.get(this.contact, "lastConversation.status") === "destroyed";
            return isDestroyed;
        },
        answerStatusButtonText() {
            return this.contact.answerStatus === "answered"
                ? this.$t("chat.gridContactList.markAsUnanswered")
                : this.$t("chat.gridContactList.markAsAnswered");
        },
        answerStatusIcon() {
            return this.contact.answerStatus === "answered" ? "close" : "done";
        },
        unreadStatusButtonText() {
            return this.contact.unread
                ? this.$t("chat.gridContactList.markAsRead")
                : this.$t("chat.gridContactList.markAsUnread");
        },
        unreadStatusIcon() {
            return this.contact.unread ? "mark_email_read" : "mark_email_unread";
        },
    },
    data: () => ({
        isPopoverVisible: false,
        visitorBanModal: false,
        privateMessage: false,
        assignAgent: false,
        forwardAgent: false,
        PHONE_UTILS,
        showSessionTerminationModal: false,
        selectedChannelId: null,
        showWebphoneSelectionModal: false,
        pendingPhoneNumber: null,
        availableWebphoneChannels: [],
        showCapiEventModal: false,
        handleKeyPress: null,
    }),
    methods: {
        ...mapActions({
            setPrivateMessageConversation: "chat/setPrivateMessageConversation",
            viewFakeConversation: "chat/viewFakeConversation",
            joinContactConversation: "chat/joinContactConversation",
            leaveContactConversation: "chat/leaveContactConversation",
            destroyContactConversation: "chat/destroyContactConversation",
            hideConversation: "chat/hideConversation",
            setVideoCallMode: "webrtc/setVideoCallMode",
            setCalling: "webrtc/setCalling",
            updateContactFields: "contact/updateContactFields",
            addNewWindow: "app/addNewWindow",
            toggleHideSystemMessages: "app/toggleHideSystemMessages",
            updateVisitorAttr: "visitor/updateVisitorAttr",
        }),
        createTask() {
            this.addNewWindow({
                type: "chat-create-task",
                title: this.$t("task.createTaskMessage"),
                payload: {
                    visitorId: this.contact._id,
                    formDefaults: {
                        title: "",
                    },
                },
            });
            this.isPopoverVisible = false;
        },
        isMetaPlatform() {
            return ["instagram", "instagram-v2", "messenger"].includes(this.contact.platform);
        },
        openCapiEventModal() {
            this.isPopoverVisible = false;
            this.showCapiEventModal = true;
        },
        callVoiceCall() {
            this.call(true);
        },
        callVideoCall() {
            this.call(false);
        },
        closeModal(modal) {
            log("closeModal", modal);
            if (modal === "assign") {
                this.assignAgent = false;
            } else if (modal === "forward") {
                this.forwardAgent = false;
            }
        },
        capitalize,
        isShowing(key) {
            let showing = true;
            const isLeak = this.contact.lastConversation.users.length === 0;
            if (key === "join-conversation") {
                showing = this.isWatching;
            } else if (key === "assign-agent") {
                showing = this.isWatching && isLeak;
            } else if (key == "forward-agent") {
                showing = !isLeak && this.contact.lastConversation.status !== "destroyed";
            } else if (key === "leave-chat") {
                showing = !this.isWatching && this.contact.lastConversation.users.length > 1;
            } else if (key === "call-features") {
                showing =
                    this.contact.platform === "web" &&
                    !this.isWatching &&
                    this.contact.lastConversation.status !== "destroyed";
            } else if (key === "close-chat") {
                showing = this.contact.lastConversation.status !== "destroyed";
            } else if (key === "more-vertical") {
                showing = this.contact.lastConversation.status !== "destroyed";
            } else if (key === "private-message") {
                showing = this.contact.lastConversation.status !== "destroyed";
            } else if (key === "auto-translate") {
                showing = this.autoTranslate && this.contact.lastConversation.status !== "destroyed";
            }
            return showing;
        },
        handleVisitorNameInputKeyDown(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this.$refs.visitorNameInput.blur();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                event.preventDefault();
            }
        },
        getVisitorAvatarSrc() {
            if (this.contact?.lastConversation?.whatsappNumber) {
                return `${CONFIG.WHATSAPP.avatarAddress}?contactId=${
                    this.contact?.lastConversation?.whatsappNumber
                }&siteId=${this.site._id}&token=${getPersistenceData("Admin-Token")}&r=${Math.random() * 1000}`;
            } else if (this.contact?.lastConversation?.instagramUserId) {
                return _.get(this.contact, "instagramInfo.profile_pic");
            } else if (this.contact.messengerUserId) {
                return _.get(this.contact, "messengerInfo.profile_pic");
            }
            return undefined;
        },
        handleVisitorNameInputKeyUp(event) {
            const el = this.$refs.visitorNameInput;
            let fullname = el.textContent.toString();
            const input = String.fromCharCode(event.keyCode);
            const isValidInput = /[a-zA-Z0-9-_ ]/.test(input);
            const isEmpty = fullname.trim().length === 0;
            log("fullname keyup", { input, isValidInput, isEmpty, fullname }, event);
            if (isEmpty && isValidInput) {
                el.textContent = input;
                setTimeout(() => {
                    const end = el.textContent.length;
                    el.setSelectionRange(end, end);
                    el.focus();
                }, 100);
            }
        },
        handleVisitorEmailInputKeyDown(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this.$refs.visitorEmailInput.blur();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                event.preventDefault();
            }
        },
        hasUserMail() {
            const email = this.getEmail();
            return !(!email || email.includes("@c.us"));
        },
        getChannelNumber() {
            return maskContactInfo("+" + this.contact.email.split("@")[0], 5);
        },
        isPhoneNumber() {
            return this.contact.fullname === this.contact.whatsappNumber;
        },
        getFullname() {
            let fullname = this.contact.fullname;
            if (!fullname) {
                fullname = this.getCustomLoginInputField(
                    this.contact.lastConversation.customLoginInfos,
                    "fullname",
                    undefined
                );
            }
            fullname = SanitizeHtml(fullname || maskContactInfo(this.contact.whatsappNumber, 5) || "-");
            return this.isPhoneNumber() ? maskContactInfo(fullname, 5) : fullname;
        },
        getEmail() {
            let email = this.contact.email;
            if (!email)
                email = this.getCustomLoginInputField(
                    this.contact.lastConversation.customLoginInfos,
                    "email",
                    undefined
                );
            return maskContactInfo(SanitizeHtml(email, true), 5);
        },
        getCustomLoginInputField(customLoginObject = [], requestedField, defaultValue) {
            let inputValues = customLoginObject
                .filter((x) => this.CustomLoginInputs[x.input])
                .map((v) => ({
                    value: v.value,
                    name: this.CustomLoginInputs[v.input].type,
                }));
            const input = inputValues.find((x) => x.name === requestedField);
            return _.get(input, "value", defaultValue);
        },
        call(soundOnly = false, screenOnly = false) {
            if (this.calling) {
                log.warn("video call request is not allowed , when another call request waiting");
                return;
            }
            log("call running");
            let config = {
                userId: this.user._id,
                secure: true,
                ssl: true,
                autocam: true,
            };
            if (!screenOnly) {
                let media = {
                    video: soundOnly ? false : { facingMode: "user" },
                    audio: true,
                };
                config = { media, ...config };
            }
            const phone = PHONE_UTILS.getPhone() || PHONE(config);
            phone.configUpdate(config);
            log("call running", { phone, soundOnly });

            PHONE_UTILS.setPhoneInstance(phone);
            phone.startcamera();
            this.setVideoCallMode({
                videoCallMode: true,
                conversationId: this.contact.lastConversation._id,
                visitorId: this.contact._id,
                soundOnly,
            });
            this.setCalling(true);
            sendSocketMessage("user video-call-request", {
                siteId: this.site._id,
                userId: this.user._id,
                visitorId: this.contact._id,
                conversationId: this.contact.lastConversation._id,
                soundOnly,
            });
        },
        cancelCall() {
            publishMessage("vc_ended", { signal: true });
        },
        assignRep() {
            this.isPopoverVisible = false;
            this.assignAgent = true;
        },
        forwardRep() {
            this.isPopoverVisible = false;
            this.forwardAgent = true;
        },
        pm() {
            this.isPopoverVisible = false;
            this.privateMessage = true;
        },
        ban() {
            this.isPopoverVisible = false;
            this.visitorBanModal = true;
        },
        leave() {
            this.$dialog
                .confirm(this.$t("message.leaveChartConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.leaveContactConversation(this.contact);
                });
        },
        runConfetti() {
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 },
            });
        },
        closeChat() {
            this.isPopoverVisible = false;
            const SESSION_TERMINATION_MODAL_ENABLED =
                this.$store.state.setting?.settings?.showSessionTerminationFormToAgents || false;

            if (SESSION_TERMINATION_MODAL_ENABLED) {
                this.showSessionTerminationModal = true;
            } else {
                this.$dialog
                    .confirm(this.$t("message.endChatConfirm"), {
                        okText: this.$t("message.continue"),
                        cancelText: this.$t("message.cancel"),
                    })
                    .then(() => {
                        this.stopVC();
                        this.destroyContactConversation(this.contact).then(() => {
                            this.runConfetti();
                        });
                    });
            }
        },
        handleSessionTerminate() {
            this.showSessionTerminationModal = false;
            this.stopVC();
            this.destroyContactConversation(this.contact).then(() => {
                this.runConfetti();
            });
        },
        handleSessionCancel() {
            this.showSessionTerminationModal = false;
        },
        stopVC() {
            log("cancelVideoCall", this.dialingVisitorId);
            if (this.dialingVisitorId && this.dialingConversationId) {
                const phone = PHONE_UTILS.getPhone();
                // dont member dialingConversationId
                sendSocketMessage("user video-call-close", {
                    siteId: this.site._id,
                    userId: this.user._id,
                    visitorId: this.dialingVisitorId,
                    conversationId: this.dialingConversationId,
                    isSoundOnly: this.soundOnly,
                });

                if (this.videoCallMode) {
                    phone.camera.stop();
                    phone.hangup(this.dialingVisitorId);
                }
                this.setVideoCallMode({
                    videoCallMode: false,
                    conversationId: null,
                    visitorId: null,
                    soundOnly: false,
                });
                this.setCalling(false);
            }
        },
        visitorBanUpdated(suppression) {
            this.updateContactFields({
                contactId: this.contact._id,
                set: {
                    suppression,
                },
            });
        },
        viewContactProfile() {
            this.addNewWindow({
                type: "show-visitor",
                title: this.contact.fullname,
                payload: {
                    visitorId: this.contact._id,
                },
                footer: false,
            });
        },
        startWebphoneCall() {
            const phoneNumber = this.contact.phone || this.contact.whatsappNumber;

            if (!phoneNumber) {
                this.$message.warning(this.$t("chat.chatCloseSettings.noPhoneNumber"));
                return;
            }

            // Kullanıcının webphone kanallarını kontrol et
            const webphoneChannels = this.getWebphoneChannels();

            if (webphoneChannels.length === 0) {
                this.$message.warning(this.$t("chat.chatCloseSettings.noWebphoneChannel"));
                return;
            }

            if (webphoneChannels.length === 1) {
                // Tek kanal varsa direkt ara
                this.initiateWebphoneCall(phoneNumber, webphoneChannels[0]._id);
            } else {
                // Birden çok kanal varsa seçim modalı göster
                this.showWebphoneChannelSelection(phoneNumber);
            }
        },

        getWebphoneChannels() {
            // Kullanıcının sahip olduğu tüm webphone kanallarını getir
            const channels = this.$store.state.channel.channels || [];
            const webphoneTypes = ['webphone-kirmizisantral', 'webphone-telnyx', 'webphone-netgsm'];

            return channels.filter(channel =>
                webphoneTypes.includes(channel.type) &&
                (channel.payload?.userId === this.user._id ||
                 channel.owner === this.user._id ||
                 this.isChannelAccessible(channel))
            );
        },

        isChannelAccessible(channel) {
            // Kanal visibility kontrolü - kullanıcı bu kanala erişebilir mi?
            if (!channel.visibility) return true;

            // User visibility kontrolü
            if (channel.visibility.users && channel.visibility.users.length > 0) {
                return channel.visibility.users.includes(this.user._id);
            }

            // Department visibility kontrolü
            if (channel.visibility.departments && channel.visibility.departments.length > 0) {
                return channel.visibility.departments.some(deptId =>
                    this.user.subjects?.some(subject => subject._id === deptId)
                );
            }

            return true;
        },

        initiateWebphoneCall(phoneNumber, channelId = null) {
            const payload = {
                phoneNumber: phoneNumber,
                contactName: this.contact.fullname || this.$t("chat.chatCloseSettings.unknown"),
                contactId: this.contact._id
            };

            if (channelId) {
                payload.channelId = channelId;
            }

            // Webphone'a arama başlatma event'i gönder
            SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.WEBPHONE_CALL_START, payload);

            this.$message.success(this.$t("chat.chatCloseSettings.webphoneCalling", { number: phoneNumber }));
        },

        showWebphoneChannelSelection(phoneNumber) {
            const webphoneChannels = this.getWebphoneChannels();

            // Modal state'ini ayarla
            this.pendingPhoneNumber = phoneNumber;
            this.availableWebphoneChannels = webphoneChannels;
            this.selectedChannelId = webphoneChannels[0]?._id || null;
            this.showWebphoneSelectionModal = true;
        },

        confirmWebphoneSelection() {
            if (this.selectedChannelId && this.pendingPhoneNumber) {
                this.initiateWebphoneCall(this.pendingPhoneNumber, this.selectedChannelId);
            }
            this.cancelWebphoneSelection();
        },

        cancelWebphoneSelection() {
            this.showWebphoneSelectionModal = false;
            this.pendingPhoneNumber = null;
            this.availableWebphoneChannels = [];
            this.selectedChannelId = null;
        },

        getChannelTypeDisplay(channelType) {
            switch (channelType) {
                case 'webphone-kirmizisantral':
                    return this.$t('chat.chatCloseSettings.channelTypeKirmiziSantral');
                case 'webphone-telnyx':
                    return this.$t('chat.chatCloseSettings.channelTypeTelnyx');
                case 'webphone-netgsm':
                    return this.$t('chat.chatCloseSettings.channelTypeNetgsm');
                default:
                    return this.$t('chat.chatCloseSettings.channelTypeWebPhone');
            }
        },

        getChannelDisplayName(channel) {
            let displayName = channel.name || this.$t('chat.chatCloseSettings.unnamedChannel');

            // Kanal tipine göre ek bilgi ekle
            if (channel.type === 'webphone-kirmizisantral') {
                displayName += ' (' + this.$t('chat.chatCloseSettings.channelTypeKirmiziSantral') + ')';
            } else if (channel.type === 'webphone-telnyx') {
                const phoneNumber = channel.payload?.callerPhoneNumber;
                if (phoneNumber) {
                    displayName += ` (${phoneNumber})`;
                }
                displayName += ' (' + this.$t('chat.chatCloseSettings.channelTypeTelnyx') + ')';
            } else if (channel.type === 'webphone-netgsm') {
                displayName += ' (' + this.$t('chat.chatCloseSettings.channelTypeNetgsm') + ')';
            }

            return displayName;
        },
        async toggleAnswerStatus() {
            if (!this.canToggleAnswerStatus) return;

            this.isPopoverVisible = false;

            const newStatus = this.contact.answerStatus === "answered" ? "unanswered" : "answered";
            const successMessage = newStatus === "answered"
                ? this.$t("chat.gridContactList.markedAsAnswered")
                : this.$t("chat.gridContactList.markedAsUnanswered");

            try {
                this.updateVisitorAttr({
                    visitorId: this.contact._id,
                    publishSocket: false,
                    payload: {
                        answerStatus: newStatus,
                    },
                });

                // Store'u güncelle
                this.updateContactFields({
                    contactId: this.contact._id,
                    set: {
                        answerStatus: newStatus,
                    },
                });

                // Local contact objesini de güncelle
                this.$set(this.contact, 'answerStatus', newStatus);

                this.$message.success(successMessage);
            } catch (error) {
                this.$message.error(this.$t("chat.gridContactList.operationFailed"));
                log.error("toggleAnswerStatus error", error);
            }
        },
        async toggleUnread() {
            this.isPopoverVisible = false;

            const newUnreadStatus = !this.contact.unread;
            const successMessage = newUnreadStatus
                ? this.$t("chat.gridContactList.markedAsUnread")
                : this.$t("chat.gridContactList.markedAsRead");

            try {
                this.updateVisitorAttr({
                    visitorId: this.contact._id,
                    payload: {
                        unread: newUnreadStatus,
                    },
                });

                this.updateContactFields({
                    contactId: this.contact._id,
                    set: {
                        unread: newUnreadStatus,
                    },
                });

                this.$set(this.contact, 'unread', newUnreadStatus);

                this.$message.success(successMessage);
            } catch (error) {
                this.$message.error(this.$t("chat.gridContactList.operationFailed"));
                log.error("toggleUnread error", error);
            }
        },
    },
    mounted() {
        this.handleKeyPress = (e) => {
            const isInput = _.get(e, "target.nodeName") === "INPUT";
            const isTextArea = _.get(e, "target.nodeName") === "TEXTAREA";
            const editableContent = !!e?.target?.getAttribute("contenteditable");

            // C tuşu için özel kontrol (meta key olmadan)
            if ((e.key === "c" || e.key === "C") && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
                if (isInput || isTextArea || editableContent) {
                    return;
                }
                if (this.canToggleAnswerStatus && !e.repeat) {
                    e.preventDefault();
                    this.toggleAnswerStatus();
                }
                return;
            }

            // Bazı aksiyonlar input içinde de çalışabilir (Forward, Private Message, Close, Block, Task, Profile)
            const allowedInInput = ['w', 'W', 'f', 'F', 'p', 'P', 'b', 'B', 't', 'T', 'o', 'O'];
            if (!allowedInInput.includes(e.key) && (isInput || isTextArea || editableContent)) {
                return;
            }

            // Ctrl + W: Görüşmeyi Sonlandır (input içinde de çalışır)
            if (e.ctrlKey && (e.key === "w" || e.key === "W") && !e.altKey && !e.shiftKey && !e.metaKey) {
                if (this.isShowing('close-chat') && !e.repeat) {
                    e.preventDefault();
                    this.closeChat();
                }
            }

            // Ctrl + F: Forward (input içinde de çalışır)
            else if (e.ctrlKey && (e.key === "f" || e.key === "F") && !e.altKey && !e.shiftKey && !e.metaKey) {
                if (this.isShowing('forward-agent') && !e.repeat) {
                    e.preventDefault();
                    this.forwardRep();
                }
            }

            // Ctrl + P: Gizli Mesaj (input içinde de çalışır)
            else if (e.ctrlKey && (e.key === "p" || e.key === "P") && !e.altKey && !e.shiftKey && !e.metaKey) {
                if (this.isShowing('private-message') && !e.repeat) {
                    e.preventDefault();
                    this.pm();
                }
            }

            // Ctrl + B: Block (input içinde de çalışır)
            else if (e.ctrlKey && (e.key === "b" || e.key === "B") && !e.altKey && !e.shiftKey && !e.metaKey) {
                if (this.checkPermission(PERMISSION_TYPES['visitor.block'].tr.name) && !e.repeat) {
                    e.preventDefault();
                    this.ban();
                }
            }

            // Ctrl + T: Task Oluştur (input içinde de çalışır)
            else if (e.ctrlKey && (e.key === "t" || e.key === "T") && !e.altKey && !e.shiftKey && !e.metaKey) {
                e.preventDefault();
                if (!e.repeat) {
                    this.createTask();
                }
            }

            // Ctrl + O: Profili Göster (input içinde de çalışır)
            else if (e.ctrlKey && (e.key === "o" || e.key === "O") && !e.altKey && !e.shiftKey && !e.metaKey) {
                e.preventDefault();
                if (!e.repeat) {
                    this.viewContactProfile();
                }
            }
        };

        document.addEventListener("keydown", this.handleKeyPress);
    },
    beforeDestroy() {
        if (this.handleKeyPress) {
            document.removeEventListener("keydown", this.handleKeyPress);
        }
    },
    watch: {
        contact: {
            deep: true,
            handler(oldVal, newVal) {
            },
        },
    },
};
</script>
<style lang="scss">
// Popover Styling
.actionSettingsButton {
    .ant-popover-arrow {
        display: none !important;
    }

    .ant-popover-inner-content {
        box-shadow: none !important;
        border: 1px solid var(--border-neutral-default);
        border-radius: 7px;
        padding: 0 !important;

        .more-actions {
            overflow: hidden;

            .action-button {
                display: flex;
                align-items: center;
                padding: 10px;
                gap: 10px;
                cursor: pointer;

                &:hover {
                    background: #f5f5f5;
                    border-radius: 4px;

                    .action-button-icon {
                        .material-icons {
                            color: #007aff;
                        }
                    }

                    .action-button-text {
                        color: #007aff;
                    }
                }

                .action-button-icon {
                    line-height: 0;

                    .material-icons {
                        font-size: 18px;
                        color: #4a4a4a;
                    }
                }

                .action-button-text {
                    font-size: 14px;
                    color: #4a4a4a;
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .keyboard-shortcut {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        padding: 2px 6px;
                        background: #f5f5f5;
                        border: 1px solid #d9d9d9;
                        border-radius: 4px;
                        font-size: 11px;
                        font-weight: 600;
                        color: #666;
                        min-width: 20px;
                        font-family: monospace;
                    }
                }
            }

            .action-button:not(:last-child) {
                border-bottom: 1px solid var(--border-neutral-default);
            }

            .action-button {
                display: flex;
                align-items: center;
                padding: 10px;
                gap: 4px;
                cursor: pointer;
                border: none !important;

                &:hover {
                    background: #f1f4f7;
                    border-radius: 4px;

                    .action-button-icon {
                        .material-icons {
                            color: #4a4a4a;
                        }
                    }

                    .action-button-text {
                        color: #4a4a4a;
                    }
                }
            }

            .close-chat-button {
                .action-button-icon {
                    .material-icons {
                        color: #fd6144 !important;
                    }
                }

                .action-button-text {
                    color: #fd6144 !important;
                }
            }
        }
    }
}
</style>
<style lang="scss" scoped>
#chatCloseSettingsButton {
    display: flex;
    justify-content: space-between;
    padding: 10px 10px 10px 10px;
    background-color: white;
    align-items: center;
    height: 50px;
    border-bottom: 1px solid var(--border-neutral-default);

    .user-meta {
        display: flex;
        align-items: center;
        gap: 5px;
        cursor: pointer;

        .contact-avatar {
            width: 40px !important;
            height: 40px !important;
            min-width: 40px !important;
            min-height: 40px !important;
            border-radius: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .chat-contact-details-wrapper {
            display: flex !important;
            flex-direction: column !important;
            gap: 2px !important;
            padding: 0 !important;
            margin: 0 !important;
            box-sizing: border-box !important;
            min-width: 0 !important;
            flex: 1 !important;
            max-width: 100% !important;
            overflow: hidden !important;

            .chat-contact-info {
                display: flex !important;
                align-items: center !important;
                gap: 2px !important;
                line-height: normal !important;
                padding: 0 !important;
                margin: 0 !important;
                box-sizing: border-box !important;
                min-width: 0 !important;

                &.chat-visitor-name-field {
                    font-size: 16px !important;
                    font-weight: bold !important;
                    display: flex !important;
                    align-items: center !important;
                    gap: 4px !important;
                    color: inherit !important;
                    text-decoration: none !important;
                    min-width: 0 !important;
                    max-width: 100% !important;
                    overflow: hidden !important;

                    .material-icons.vip {
                        font-size: 18px !important;
                        background: linear-gradient(#d6ea2d, #f960cb, #9037d4) !important;
                        background: -webkit-linear-gradient(#d6ea2d, #f960cb, #9037d4) !important;
                        background-clip: text !important;
                        -webkit-background-clip: text !important;
                        color: transparent !important;
                        -webkit-text-fill-color: transparent !important;
                        line-height: 1 !important;
                        flex-shrink: 0 !important;
                    }

                    .visitor-name-text {
                        white-space: nowrap !important;
                        overflow: hidden !important;
                        text-overflow: ellipsis !important;
                        min-width: 0 !important;
                        flex: 1 !important;
                        max-width: 500px;
                    }
                }

                &.chat-visitor-email-field {
                    margin-top: -5px !important;
                    font-size: 14px !important;
                    color: var(--color-text-info-default) !important;

                    & > div,
                    & span,
                    & a {
                        color: var(--color-text-info-default) !important;
                        text-decoration: none !important;
                        line-height: normal !important;
                    }

                    & a:hover {
                        text-decoration: underline !important;
                    }

                    .material-icons {
                        font-size: 18px !important;
                        line-height: 1 !important;
                        color: inherit !important;
                    }
                }

                &.chat-visitor-platform-field {
                    font-size: 11px !important;
                    color: #9ca3af !important;
                    font-weight: 400 !important;
                    margin-top: -2px !important;
                    padding: 0 !important;
                    overflow: hidden !important;
                    max-width: 100% !important;
                    min-width: 0 !important;
                    flex: 1 !important;
                    width: 100% !important;

                    .chat-platform-info-wrapper {
                        display: flex !important;
                        align-items: center !important;
                        justify-content: flex-start !important;
                        gap: 6px !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        width: 100% !important;
                        max-width: 100% !important;
                        overflow: hidden !important;
                        min-width: 0 !important;

                        .chat-platform-text-label {
                            margin: 0 !important;
                            padding: 0 !important;
                            font-size: 11px !important;
                            color: #9ca3af !important;
                            font-weight: 400 !important;
                            line-height: normal !important;
                            white-space: nowrap !important;
                            overflow: hidden !important;
                            text-overflow: ellipsis !important;
                            max-width: 150px !important;
                        }

                        .chat-platform-separator {
                            font-size: 11px !important;
                            color: #9ca3af !important;
                            margin: 0 2px !important;
                            flex-shrink: 0 !important;
                            white-space: nowrap !important;
                        }

                        .chat-contact-icon {
                            font-size: 12px !important;
                            line-height: 1 !important;
                            color: #9ca3af !important;
                            flex-shrink: 0 !important;
                        }

                        .chat-platform-email-link,
                        .chat-platform-phone-link {
                            display: block !important;
                            white-space: nowrap !important;
                            overflow: hidden !important;
                            text-overflow: ellipsis !important;
                            max-width: 150px !important;

                            a {
                                font-size: 11px !important;
                                color: #9ca3af !important;
                                text-decoration: none !important;
                                line-height: normal !important;
                                display: block !important;
                                white-space: nowrap !important;
                                overflow: hidden !important;
                                text-overflow: ellipsis !important;
                                max-width: 100% !important;
                            }

                            a:hover {
                                text-decoration: underline !important;
                                color: #6b7280 !important;
                            }
                        }

                        span, a {
                            font-size: 11px !important;
                            color: #9ca3af !important;
                            text-decoration: none !important;
                            line-height: normal !important;
                        }

                        a:hover {
                            text-decoration: underline !important;
                            color: #6b7280 !important;
                        }
                    }
                }
            }
        }
    }

    .conversation-actions {
        display: flex;
        align-items: center;
        gap: 10px;

        &-button {
            cursor: pointer;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            line-height: 0;
            user-select: none;

            .material-icons {
                color: var(--text-main-default) !important;
            }

            &.call {
                .material-icons {
                    font-size: 20px;
                }
            }

            &.video-call {
                .material-icons {
                    font-size: 24px;
                }
            }

            &.more-vert {
                .material-icons {
                    font-size: 24px;
                }
            }

            &.webphone-call {
                width: 24px;
                height: 24px;
                border-radius: 50%;
                background-color: #34c759;
                transition: all 0.2s ease;

                .material-icons {
                    font-size: 16px;
                    color: white;
                }

                &:hover {
                    background-color: #2fb653;
                    transform: scale(1.1);
                    box-shadow: 0 2px 8px rgba(52, 199, 89, 0.3);
                }
            }
        }

        .call-features {
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }
}
</style>

<style lang="scss">
.session-termination-modal {
    .ant-modal {
        top: 10px !important;
    }
}

.webphone-selection-modal {
    .webphone-channel-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: 8px;

        .channel-option {
            display: flex;
            align-items: center;
            padding: 16px;
            border: 2px solid #e8eef3;
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
            background: #fff;
            position: relative;

            &:hover {
                background: #f8f9fa;
                border-color: #007aff;
                transform: translateY(-2px);
                box-shadow: 0 4px 15px rgba(0, 122, 255, 0.15);
            }

            &.selected {
                background: #f0f8ff;
                border-color: #007aff;
                box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);

                .channel-radio input {
                    background: #007aff;
                    border-color: #007aff;

                    &::after {
                        content: '';
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        width: 6px;
                        height: 6px;
                        border-radius: 50%;
                        background: white;
                    }
                }

                .channel-arrow i {
                    color: #007aff;
                }
            }

            .channel-radio {
                margin-right: 12px;
                position: relative;

                input[type="radio"] {
                    width: 18px;
                    height: 18px;
                    border: 2px solid #d9d9d9;
                    border-radius: 50%;
                    appearance: none;
                    cursor: pointer;
                    position: relative;
                    transition: all 0.2s ease;

                    &:checked {
                        background: #007aff;
                        border-color: #007aff;
                    }
                }
            }

            .channel-icon {
                width: 48px;
                height: 48px;
                margin-right: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #f5f5f5;
                border-radius: 12px;
                overflow: hidden;

                img {
                    width: 32px;
                    height: 32px;
                    object-fit: contain;
                }

                .webphone-icon {
                    font-size: 24px;
                    color: #d32f2f;
                }
            }

            .channel-info {
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 4px;

                .channel-name {
                    font-weight: 600;
                    color: #1c1c1e;
                    font-size: 16px;
                    line-height: 1.2;
                }

                .channel-details {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 13px;
                    color: #8e8e93;

                    .channel-type {
                        font-weight: 500;
                    }

                    .channel-phone {
                        color: #007aff;
                        font-weight: 500;
                    }
                }
            }

            .channel-arrow {
                margin-left: 12px;

                i {
                    font-size: 20px;
                    color: #c7c7cc;
                    transition: all 0.2s ease;
                }
            }
        }
    }

    .ant-modal-content {
        overflow: visible;
    }
}

// Dark mode support
@media (prefers-color-scheme: dark) {
    .webphone-selection-modal {
        .webphone-channel-list {
            .channel-option {
                background: #2c2c2e;
                border-color: #3a3a3c;

                &:hover {
                    background: #3a3a3c;
                    border-color: #007aff;
                }

                &.selected {
                    background: #1e3a5f;
                    border-color: #007aff;
                }

                .channel-icon {
                    background: #3a3a3c;
                }

                .channel-info {
                    .channel-name {
                        color: white;
                    }
                }

                .channel-radio input[type="radio"] {
                    border-color: #8e8e93;
                }
            }
        }
    }
}
</style>
